"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { CircleCheck, CircleSlash, PieChart, Repeat, UtensilsCrossed } from "lucide-react"
import type { MealInstance, MealOutcomeRequest, MealOutcomeResponse } from "@bitebalance/contracts"

type MealOutcomeStatus = MealOutcomeRequest["outcome"]
import { useApiClient } from "@/api/useApiClient"
import { humanizeRecipeId, slotLabel } from "@/components/plan/recipeDisplay"
import { useI18n } from "@/i18n"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { openAmyWithPrompt } from "@/lib/amyLauncher"

export interface OutcomeSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  meal: MealInstance | null
}

/**
 * Meal outcome sheet — 2026-08-30 owner redesign + BUG FIX.
 *
 * THE BUG ("I marked one meal and the whole day was marked the same"): the
 * sheet is shared across meals but only reset itself on CLOSE — switching
 * straight from meal A to meal B kept A's success state on screen, so every
 * subsequently opened meal looked already-recorded. State now resets
 * whenever the TARGET MEAL changes, and the header names the meal being
 * marked so there is never ambiguity. Only the server rows are truth — one
 * POST per explicit save, exactly as before.
 *
 * The redesign: big icon tiles with spring selection, a drawn-in success
 * check with the meal's name, staggered entrance — reduced-motion aware.
 */
/** "Wednesday, 7 September" / "Mittwoch, 7. September" — the meal's own date in words, for the Amy prefill only (never a wire id). */
function dateInWords(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  }).format(new Date(`${iso}T12:00:00Z`))
}

export function OutcomeSheet({ open, onOpenChange, meal }: OutcomeSheetProps) {
  const api = useApiClient()
  const { t, locale } = useI18n()
  const reduce = useReducedMotion()
  const [selected, setSelected] = useState<MealOutcomeStatus | null>(null)
  // 2026-09-02 acceptance: "partly ate it" must capture HOW MUCH — the
  // server counts only what was actually eaten, never an invented 50%.
  const [partialFraction, setPartialFraction] = useState<number | null>(null)
  // Release-readiness P1, item A (2026-09-21): the "changed" replacement
  // capture — see ReplacementCaptureStep.tsx (mobile) for the same design.
  const [changedFood, setChangedFood] = useState("")
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle")
  const [pantrySync, setPantrySync] = useState<MealOutcomeResponse["pantrySync"]>(null)

  // BUG FIX: a NEW target meal always starts from a clean slate — the old
  // close-only reset let one meal's success state leak onto the next.
  useEffect(() => {
    setSelected(null)
    setPartialFraction(null)
    setChangedFood("")
    setStatus("idle")
    setPantrySync(null)
  }, [meal?.id])

  async function handleSubmit() {
    if (!meal || !selected) return
    if (selected === "partial" && partialFraction === null) return
    setStatus("submitting")
    try {
      const result = await api.recordMealOutcome(meal.id, {
        outcome: selected,
        ...(selected === "partial" && partialFraction !== null
          ? { actualServings: partialFraction * meal.servings }
          : {}),
      })
      setPantrySync(result.pantrySync ?? null)
      setStatus("success")
    } catch {
      setStatus("error")
    }
  }

  // Release-readiness P1, item A: "Log it with Amy" NEVER calls the API
  // itself — it closes this sheet, then hands Amy a plain-language prompt
  // (food/meal/slot/date, never a meal id) through the SAME pub-sub
  // TodayPage's own "review my progress" CTA uses. Amy resolves
  // `replaces_meal_id` from her own turn context and calls
  // `log_off_plan_food` → `POST /v1/ledger/entries` on confirmation — the
  // off-plan entry and the "changed" outcome land atomically. No new
  // mutation path is added here.
  function handleLogWithAmy() {
    if (!meal || !changedFood.trim()) return
    const prompt = t("views.outcomeSheet.changedAmyPrefill", {
      food: changedFood.trim(),
      meal: meal.title?.en ?? humanizeRecipeId(meal.recipeId),
      slot: slotLabel(meal.slot, t),
      date: dateInWords(meal.date, locale),
    })
    onOpenChange(false)
    openAmyWithPrompt(prompt)
  }

  const OPTIONS: Array<{
    value: MealOutcomeStatus
    icon: typeof CircleCheck
    accent: string
  }> = [
    { value: "had", icon: CircleCheck, accent: "text-green" },
    { value: "partial", icon: PieChart, accent: "text-warning" },
    { value: "skipped", icon: CircleSlash, accent: "text-ink-2" },
    { value: "changed", icon: Repeat, accent: "text-danger" },
  ]

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <UtensilsCrossed className="size-4 text-green" aria-hidden="true" />
            {t("views.outcomeSheet.title")}
          </SheetTitle>
          {/* Always name the meal being marked — never ambiguous. */}
          {meal && (
            <p className="text-sm font-semibold text-ink">
              {meal.title?.en ?? humanizeRecipeId(meal.recipeId)}
            </p>
          )}
        </SheetHeader>
        <div className="flex flex-col gap-3 px-4 pb-4">
          <AnimatePresence mode="wait" initial={false}>
            {status === "success" ? (
              <motion.div
                key="success"
                initial={reduce ? false : { opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 22 }}
                className="flex flex-col gap-2 rounded-bb-lg border border-green/30 bg-green/5 p-4"
              >
                <p role="status" className="flex items-center gap-2 text-sm font-medium text-green">
                  <motion.span
                    initial={reduce ? false : { scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 15, delay: 0.1 }}
                  >
                    <CircleCheck className="size-5 shrink-0" aria-hidden="true" />
                  </motion.span>
                  {t("views.outcomeSheet.success")}
                </p>
                {pantrySync && pantrySync.deductedIngredients > 0 && (
                  <p role="status" className="text-sm text-green">
                    {t("views.outcomeSheet.pantryUpdated", {
                      count: String(pantrySync.deductedIngredients),
                    })}
                  </p>
                )}
                {pantrySync && pantrySync.restoredIngredients > 0 && (
                  <p role="status" className="text-sm text-green">
                    {t("views.outcomeSheet.pantryRestored", {
                      count: String(pantrySync.restoredIngredients),
                    })}
                  </p>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="picker"
                initial={false}
                exit={reduce ? undefined : { opacity: 0, y: -6 }}
                className="flex flex-col gap-3"
              >
                <div
                  className="grid grid-cols-2 gap-2 sm:grid-cols-4"
                  role="radiogroup"
                  aria-label={t("views.outcomeSheet.title")}
                >
                  {OPTIONS.map(({ value, icon: Icon, accent }, i) => {
                    const isSelected = selected === value
                    return (
                      <motion.button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        onClick={() => {
                          setSelected(value)
                          if (value !== "partial") setPartialFraction(null)
                        }}
                        initial={reduce ? false : { opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: reduce ? 0 : i * 0.05 }}
                        whileTap={reduce ? undefined : { scale: 0.94 }}
                        className={`flex min-h-20 flex-col items-center justify-center gap-1.5 rounded-bb-lg border px-2 py-3 transition-all duration-150 ${
                          isSelected
                            ? "border-green bg-green/10 shadow-sm"
                            : "border-line bg-paper hover:border-green/50"
                        }`}
                      >
                        <Icon className={`size-5 ${accent}`} aria-hidden="true" />
                        <span className="text-xs font-semibold text-ink">
                          {t(`views.outcomeSheet.${value}`)}
                        </span>
                      </motion.button>
                    )
                  })}
                </div>
                {selected === "partial" && (
                  <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium text-ink">
                      {t("views.outcomeSheet.partialAmountQuestion")}
                    </p>
                    <div
                      className="flex gap-2"
                      role="radiogroup"
                      aria-label={t("views.outcomeSheet.partialAmountQuestion")}
                    >
                      {([0.25, 0.5, 0.75] as const).map(fraction => (
                        <button
                          key={fraction}
                          type="button"
                          role="radio"
                          aria-checked={partialFraction === fraction}
                          onClick={() => setPartialFraction(fraction)}
                          className={`min-h-11 flex-1 rounded-bb-lg border text-base font-semibold transition-colors ${
                            partialFraction === fraction
                              ? "border-green bg-green/10 text-ink"
                              : "border-line bg-paper text-ink hover:border-green/50"
                          }`}
                        >
                          {fraction === 0.25 ? "¼" : fraction === 0.5 ? "½" : "¾"}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Release-readiness P1, item A (2026-09-21): "changed" reveals
                    the replacement-capture field — same shape as "partial"'s
                    fraction reveal above. Writes nothing itself; see the two
                    footer actions below (handleLogWithAmy / handleSubmit). */}
                {selected === "changed" && (
                  <div className="flex flex-col gap-2">
                    <label htmlFor="changed-food" className="text-sm font-medium text-ink">
                      {t("views.outcomeSheet.changedWhatInstead")}
                    </label>
                    <Textarea
                      id="changed-food"
                      value={changedFood}
                      onChange={e => setChangedFood(e.target.value)}
                      rows={3}
                      autoFocus
                    />
                    <p className="text-xs text-ink-2">{t("views.outcomeSheet.changedBeta")}</p>
                  </div>
                )}
                <p className="text-xs text-ink-2">
                  {t("views.outcomeSheet.pantryHint")}{" "}
                  <Link to="/app/pantry" className="underline hover:text-ink">
                    {t("views.outcomeSheet.pantryHintLink")}
                  </Link>
                </p>
                {status === "error" && (
                  <p role="alert" className="text-sm text-destructive">
                    {t("views.outcomeSheet.error")}
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <SheetFooter>
          {status !== "success" && selected === "changed" ? (
            <>
              {/* Release-readiness P1, item A: "changed" replaces the normal
                  Save-outcome button with the two capture actions — never
                  both, so a bare recordMealOutcome("changed") only ever
                  happens through the explicit Skip below. */}
              <Button
                type="button"
                className="min-h-11 rounded-bb-full"
                disabled={!changedFood.trim()}
                onClick={handleLogWithAmy}
              >
                {t("views.outcomeSheet.changedLogWithAmy")}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                disabled={status === "submitting"}
                onClick={() => void handleSubmit()}
              >
                {status === "submitting"
                  ? t("views.outcomeSheet.submitting")
                  : t("views.outcomeSheet.changedSkip")}
              </Button>
            </>
          ) : (
            status !== "success" && (
              <Button
                type="button"
                className="min-h-11 rounded-bb-full"
                disabled={
                  !selected ||
                  status === "submitting" ||
                  (selected === "partial" && partialFraction === null)
                }
                onClick={() => void handleSubmit()}
              >
                {status === "submitting"
                  ? t("views.outcomeSheet.submitting")
                  : t("views.outcomeSheet.submit")}
              </Button>
            )
          )}
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            onClick={() => onOpenChange(false)}
          >
            {t("views.outcomeSheet.close")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
